import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Loader from "../../../common/Loader";

const EditCourse = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [image, setImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    category: "",
    name: "",
    instituteName: "International School of Designing",
    description: "",
    location: "",
    universityCollaboration: "",
    careerPaths: [""],
    programs: [
      {
        programType: "",
        duration: "",
        eligibility: "",
        credits: "",
        onlineApplicationFee: "",
        curriculum: [
          {
            year: "",
            semester: "",
            credits: "",
            subjects: [
              {
                name: "",
                type: "theory",
              },
            ],
          },
        ],
      },
    ],
  });

  useEffect(() => {
    fetchCourseData();
  }, [id]);

  const fetchCourseData = async () => {
    try {
      setFetchingData(true);
      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/courses/get-course-by-id/${id}`,
        headers: {},
      };
      const startTime = Date.now();

      const response = await axios.request(config);
      const courseData = response.data.data;
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(1000 - elapsed, 0);

      setFormData({
        category: courseData.category || "",
        name: courseData.name || "",
        instituteName:
          courseData.instituteName || "International School of Designing",
        description: courseData.description || "",
        location: courseData.location || "",
        universityCollaboration: courseData.universityCollaboration || "",
        careerPaths:
          courseData.careerPaths?.length > 0 ? courseData.careerPaths : [""],
        programs:
          courseData.programs?.length > 0
            ? courseData.programs.map((program) => ({
                programType: program.programType || "",
                duration: program.duration || "",
                eligibility: program.eligibility || "",
                credits: program.credits || "",
                onlineApplicationFee: program.onlineApplicationFee || "",
                curriculum:
                  program.curriculum?.length > 0
                    ? program.curriculum.map((sem) => ({
                        year: sem.year || "",
                        semester: sem.semester || "",
                        credits: sem.credits || "",
                        subjects:
                          sem.subjects?.length > 0
                            ? sem.subjects.map((sub) => ({
                                name: sub.name || "",
                                type: sub.type || "theory",
                              }))
                            : [{ name: "", type: "theory" }],
                      }))
                    : [
                        {
                          year: "",
                          semester: "",
                          credits: "",
                          subjects: [{ name: "", type: "theory" }],
                        },
                      ],
              }))
            : [
                {
                  programType: "",
                  duration: "",
                  eligibility: "",
                  credits: "",
                  onlineApplicationFee: "",
                  curriculum: [
                    {
                      year: "",
                      semester: "",
                      credits: "",
                      subjects: [{ name: "", type: "theory" }],
                    },
                  ],
                },
              ],
      });
      setImage(courseData.img || null);
      setTimeout(() => setFetchingData(false), remaining);
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Failed to fetch course data.",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
      navigate("/courses");
    } finally {
      setTimeout(() => setFetchingData(false), 1000);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCareerPathChange = (index, value) => {
    const newCareerPaths = [...formData.careerPaths];
    newCareerPaths[index] = value;
    setFormData((prev) => ({
      ...prev,
      careerPaths: newCareerPaths,
    }));
  };

  const addCareerPath = () => {
    setFormData((prev) => ({
      ...prev,
      careerPaths: [...prev.careerPaths, ""],
    }));
  };

  const removeCareerPath = (index) => {
    const newCareerPaths = formData.careerPaths.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      careerPaths: newCareerPaths,
    }));
  };

  // Program Management
  const handleProgramChange = (progIndex, field, value) => {
    const newPrograms = [...formData.programs];
    newPrograms[progIndex][field] = value;
    setFormData((prev) => ({
      ...prev,
      programs: newPrograms,
    }));
  };

  const addProgram = () => {
    setFormData((prev) => ({
      ...prev,
      programs: [
        ...prev.programs,
        {
          programType: "",
          duration: "",
          eligibility: "",
          credits: "",
          onlineApplicationFee: "",
          curriculum: [
            {
              year: "",
              semester: "",
              credits: "",
              subjects: [
                {
                  name: "",
                  type: "theory",
                },
              ],
            },
          ],
        },
      ],
    }));
  };

  const removeProgram = (index) => {
    const newPrograms = formData.programs.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      programs: newPrograms,
    }));
  };

  // Curriculum Management
  const handleSubjectChange = (progIndex, semIndex, subIndex, field, value) => {
    const newPrograms = [...formData.programs];
    newPrograms[progIndex].curriculum[semIndex].subjects[subIndex][field] =
      value;
    setFormData((prev) => ({
      ...prev,
      programs: newPrograms,
    }));
  };

  const addSubject = (progIndex, semIndex) => {
    const newPrograms = [...formData.programs];
    newPrograms[progIndex].curriculum[semIndex].subjects.push({
      name: "",
      type: "theory",
    });
    setFormData((prev) => ({
      ...prev,
      programs: newPrograms,
    }));
  };

  const removeSubject = (progIndex, semIndex, subIndex) => {
    const newPrograms = [...formData.programs];
    newPrograms[progIndex].curriculum[semIndex].subjects = newPrograms[
      progIndex
    ].curriculum[semIndex].subjects.filter((_, i) => i !== subIndex);
    setFormData((prev) => ({
      ...prev,
      programs: newPrograms,
    }));
  };

  const handleSemesterChange = (progIndex, semIndex, field, value) => {
    const newPrograms = [...formData.programs];
    newPrograms[progIndex].curriculum[semIndex][field] = value;
    setFormData((prev) => ({
      ...prev,
      programs: newPrograms,
    }));
  };

  const addSemester = (progIndex) => {
    const newPrograms = [...formData.programs];
    newPrograms[progIndex].curriculum.push({
      year: "",
      semester: "",
      credits: "",
      subjects: [
        {
          name: "",
          type: "theory",
        },
      ],
    });
    setFormData((prev) => ({
      ...prev,
      programs: newPrograms,
    }));
  };

  const removeSemester = (progIndex, semIndex) => {
    const newPrograms = [...formData.programs];
    newPrograms[progIndex].curriculum = newPrograms[
      progIndex
    ].curriculum.filter((_, i) => i !== semIndex);
    setFormData((prev) => ({
      ...prev,
      programs: newPrograms,
    }));
  };

  const handleImageUpload = async (file) => {
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", process.env.REACT_APP_UPLOAD_PRESET);

      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_NAME}/image/upload`,
        formData
      );

      setImage(response.data.secure_url);
      toast.success("Image uploaded successfully!");
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error("Failed to upload image. Please try again.");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!image) {
      toast.error("Please upload a course image");
      return;
    }
    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        img: image,
        careerPaths: formData.careerPaths.filter((path) => path.trim() !== ""),
        programs: formData.programs.map((program) => ({
          ...program,
          credits: parseInt(program.credits) || 0,
          onlineApplicationFee: parseInt(program.onlineApplicationFee) || 0,
          curriculum: program.curriculum.map((sem) => ({
            ...sem,
            credits: parseInt(sem.credits) || 0,
            subjects: sem.subjects.filter((sub) => sub.name.trim() !== ""),
          })),
        })),
      };

      const config = {
        method: "put",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/courses/update/${id}`,
        headers: {
          "Content-Type": "application/json",
        },
        data: JSON.stringify(dataToSend),
      };

      const response = await axios.request(config);

      toast.success("Course updated successfully!", {
        position: "top-right",
        autoClose: 2000,
      });

      navigate("/courses");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message ||
          "Failed to update course. Please try again.",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return <Loader />;
  }

  return (
    <div className="w-full mx-auto rounded-lg shadow-md p-6">
      <h1 className="text-3xl sm:text-4xl font-bold text-primaryTextColor mb-4 mt-6">
        Edit Course
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-secondaryTextColor">
            Basic Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Course Name *
              </label>
              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Section Header *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Course Image {!image && "*"}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                required={!image}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
              {uploadingImage && (
                <p className="text-sm text-blue-600 mt-1">Uploading image...</p>
              )}
              {image && (
                <div className="mt-2">
                  <img
                    src={image}
                    alt="Course preview"
                    className="h-20 w-20 object-cover rounded"
                  />
                  <p className="text-sm text-gray-600 mt-1">
                    Image uploaded successfully. Upload a new one to replace.
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Location *
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondaryTextColor mb-1">
              University Collaboration
            </label>
            <input
              type="text"
              name="universityCollaboration"
              value={formData.universityCollaboration}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondaryTextColor mb-1">
              Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
            />
          </div>
        </div>

        {/* Programs */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-secondaryTextColor">
              Programs
            </h2>
            <button
              type="button"
              onClick={addProgram}
              className="px-4 py-2 bg-secondaryColor text-white rounded-md hover:bg-opacity-90 transition-opacity"
            >
              Add Program
            </button>
          </div>

          {formData.programs.map((program, progIndex) => (
            <div
              key={progIndex}
              className="border border-gray-300 rounded-md p-4 space-y-4"
            >
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-secondaryTextColor">
                  Program {progIndex + 1}
                </h3>
                {formData.programs.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeProgram(progIndex)}
                    className="px-3 py-1 bg-primaryColor text-white text-sm rounded-md hover:bg-opacity-90 transition-opacity"
                  >
                    Remove Program
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                    Program Type *
                  </label>
                  <select
                    value={program.programType}
                    onChange={(e) =>
                      handleProgramChange(
                        progIndex,
                        "programType",
                        e.target.value
                      )
                    }
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
                  >
                    <option value="">Select Type</option>
                    <option value="BACHELORS">Bachelors</option>
                    <option value="MASTERS">Masters</option>
                    <option value="DIPLOMA">Diploma</option>
                    <option value="CERTIFICATE">Certificate</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                    Duration *
                  </label>
                  <input
                    type="text"
                    value={program.duration}
                    onChange={(e) =>
                      handleProgramChange(progIndex, "duration", e.target.value)
                    }
                    required
                    placeholder="e.g., 4 Years"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                    Eligibility *
                  </label>
                  <input
                    type="text"
                    value={program.eligibility}
                    onChange={(e) =>
                      handleProgramChange(
                        progIndex,
                        "eligibility",
                        e.target.value
                      )
                    }
                    required
                    placeholder="e.g., 10+2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                    Total Credits *
                  </label>
                  <input
                    type="number"
                    value={program.credits}
                    onChange={(e) =>
                      handleProgramChange(progIndex, "credits", e.target.value)
                    }
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                    Application Fee *
                  </label>
                  <input
                    type="number"
                    value={program.onlineApplicationFee}
                    onChange={(e) =>
                      handleProgramChange(
                        progIndex,
                        "onlineApplicationFee",
                        e.target.value
                      )
                    }
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
                  />
                </div>
              </div>

              {/* Curriculum for this program */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="text-md font-medium text-secondaryTextColor">
                    Curriculum
                  </h4>
                  <button
                    type="button"
                    onClick={() => addSemester(progIndex)}
                    className="px-3 py-1 bg-secondaryColor text-white text-sm rounded-md hover:bg-opacity-90 transition-opacity"
                  >
                    Add Semester
                  </button>
                </div>

                {program.curriculum.map((semester, semIndex) => (
                  <div
                    key={semIndex}
                    className="border border-gray-200 rounded-md p-3 space-y-3"
                  >
                    <div className="flex justify-between items-center">
                      <h5 className="text-sm font-medium text-secondaryTextColor">
                        Semester {semIndex + 1}
                      </h5>
                      {program.curriculum.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSemester(progIndex, semIndex)}
                          className="px-2 py-1 bg-primaryColor text-white text-xs rounded-md hover:bg-opacity-90 transition-opacity"
                        >
                          Remove Semester
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-secondaryTextColor mb-1">
                          Year *
                        </label>
                        <input
                          type="text"
                          value={semester.year}
                          onChange={(e) =>
                            handleSemesterChange(
                              progIndex,
                              semIndex,
                              "year",
                              e.target.value
                            )
                          }
                          required
                          placeholder="e.g., 1st Year"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-secondaryTextColor mb-1">
                          Semester Name *
                        </label>
                        <input
                          type="text"
                          value={semester.semester}
                          onChange={(e) =>
                            handleSemesterChange(
                              progIndex,
                              semIndex,
                              "semester",
                              e.target.value
                            )
                          }
                          required
                          placeholder="e.g., 1st Semester"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-secondaryTextColor mb-1">
                          Credits *
                        </label>
                        <input
                          type="number"
                          value={semester.credits}
                          onChange={(e) =>
                            handleSemesterChange(
                              progIndex,
                              semIndex,
                              "credits",
                              e.target.value
                            )
                          }
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h6 className="text-sm font-medium text-secondaryTextColor">
                        Subjects
                      </h6>
                      {semester.subjects.map((subject, subIndex) => (
                        <div key={subIndex} className="flex gap-2">
                          <input
                            type="text"
                            value={subject.name}
                            onChange={(e) =>
                              handleSubjectChange(
                                progIndex,
                                semIndex,
                                subIndex,
                                "name",
                                e.target.value
                              )
                            }
                            placeholder="Subject name"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
                          />
                          <select
                            value={subject.type}
                            onChange={(e) =>
                              handleSubjectChange(
                                progIndex,
                                semIndex,
                                subIndex,
                                "type",
                                e.target.value
                              )
                            }
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
                          >
                            <option value="theory">Theory</option>
                            <option value="practical">Practical</option>
                          </select>
                          {semester.subjects.length > 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                removeSubject(progIndex, semIndex, subIndex)
                              }
                              className="px-3 py-2 bg-primaryColor text-white rounded-md hover:bg-opacity-90 transition-opacity"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addSubject(progIndex, semIndex)}
                        className="px-3 py-1 bg-secondaryColor text-white text-sm rounded-md hover:bg-opacity-90 transition-opacity"
                      >
                        Add Subject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Career Paths */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-secondaryTextColor">
            Career Paths
          </h2>
          {formData.careerPaths.map((path, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={path}
                onChange={(e) => handleCareerPathChange(index, e.target.value)}
                placeholder="Enter career path"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
              {formData.careerPaths.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeCareerPath(index)}
                  className="px-3 py-2 bg-primaryColor text-white rounded-md hover:bg-opacity-90 transition-opacity"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addCareerPath}
            className="px-4 py-2 bg-secondaryColor text-white rounded-md hover:bg-opacity-90 transition-opacity"
          >
            Add Career Path
          </button>
        </div>

        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate("/courses")}
            className="px-6 py-3 bg-gray-500 text-white font-medium rounded-md hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-primaryBtnColor text-white font-medium rounded-md hover:bg-opacity-90 transition-opacity disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? "Updating Course..." : "Update Course"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditCourse;
